import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
    MAX_DAYS_RANGE,
    PRESELECTED_DAYS,
    ITEMS_PER_PAGE,
    DEFAULT_FILE_TYPE,
    FILE_TYPE_DCP,
    FILE_TYPE_DCPARAMSBAK,
    FILE_TYPE_DCPARAMSBAK_LABEL
} from './constants';
import { GeneralDialog, TimeSelectorRow, LoadingStatusWrapper, GeneralButtonDropDown } from 'sharedComponents';
import { GeneralTable, sortingDirections } from 'sharedComponents/GeneralTable';
import { getColumns } from './helpers';
import { useNewCapability } from 'sharedHooks';


import './styles.scss';
import { apiCallStatus } from 'helpers/constants';

const ParameterBackupModal = (props) => {

    const { t: translate } = useTranslation();
    const capability = useNewCapability();

    const [from, setFrom] = useState();
    const [to, setTo] = useState();

    useEffect(() => {
        if (props?.model?.selectedAsset?.id) {
            props?.actions?.getParameterBackupTypes(props.model.selectedAsset.id);
        }
        return () => {
            return props.actions.clearParameterFileData();
        };
    }, []);

    useEffect(() => {
        props.actions.getParameterFileListForAsset(props.model.selectedAsset?.id, from, to);
    }, [from, to]);

    const handleSelectDate = (from, to) => {
        if (from && to) {
            setFrom(from);
            setTo(to);
        }
    };

    const onDownloadFile = (fileId) => {
        props.actions.getParameterFileWithId(props.model.selectedAsset, fileId,
            {
                success: translate('ABB.Powertrain.Frontend.parameterBackupDownloadSuccessMessage'),
                error: translate('ABB.Powertrain.Frontend.parameterBackupErrorMessage'),
            });
    };

    const fileList = props?.model?.parameterFileList?.data || [];
    const typeList = props?.model?.parameterFileTypeList?.data || [];

    const getFileTypeLabel = (type) => {
        const normalizedType = String(type || '').trim().toLowerCase();

        if (normalizedType === FILE_TYPE_DCP || normalizedType === FILE_TYPE_DCPARAMSBAK) {
            return FILE_TYPE_DCPARAMSBAK_LABEL;
        }

        return String(type || '').toUpperCase();
    };

    return (
        <GeneralDialog
            modalClassName='parameter-backup-modal'
            show={props.show}
            close={props.close}
            title={props.title}
            closeButton={true}
        >
            <div className='modal-form parameter-backup-form'>
                <div className='parameter-backup-header-row'>
                    <TimeSelectorRow
                        fromDate={from}
                        toDate={to}
                        handleSelectDate={handleSelectDate}
                        maxDaysBack={MAX_DAYS_RANGE}
                        preSelectedDays={PRESELECTED_DAYS}
                        numberOfMonths={2}
                        footerIntervalText={translate('ABB.Powertrain.Frontend.datePickerMax90DaysNote')}
                    />
                    {capability?.hasParameterBackupTriggerCapability() && <GeneralButtonDropDown
                        type='primary'
                        className='trigger-parameter-backup-button'
                        text={translate('ABB.Powertrain.Frontend.TriggerParameterBackupLabel')}
                        options={(typeList?.length > 0 ? typeList : [DEFAULT_FILE_TYPE]).map(type => ({
                            label: getFileTypeLabel(type),
                            value: type
                        }))}
                        onSelect={(fileType) => props.actions.triggerParameterBackup(props?.asset?.id, fileType)}
                        disabled={props.model.triggerParameterBackupStatus === apiCallStatus.LOADING || props.model.parameterFileListLoadingStatus === apiCallStatus.LOADING}
                    />}
                </div>
                <LoadingStatusWrapper useCustomAnchor loadingStatus={[props.model.parameterFileListLoadingStatus, props.model.triggerParameterBackupStatus]} hideError>
                    {props.model.parameterFileListLoadingStatus !== apiCallStatus.LOADING ?
                        <GeneralTable
                            className='parameter-table'
                            tableKey='id'
                            columns={getColumns(translate, onDownloadFile)}
                            data={fileList}
                            emptyText={translate('ABB.Powertrain.Frontend.noDataLabel')}
                            scroll={{ x: 560, y: null }}
                            type='primary'
                            paginationProps={{ pageSize: ITEMS_PER_PAGE }}
                            initialSortingProps={{
                                column: 'timestamp',
                                direction: sortingDirections.DESC
                            }}
                        /> : null}
                </LoadingStatusWrapper>
            </div>
        </GeneralDialog>
    );
};

ParameterBackupModal.propTypes = {
    actions: PropTypes.object.isRequired,
    model: PropTypes.object.isRequired,
    close: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    show: PropTypes.bool.isRequired,
    asset: PropTypes.shape({
        id: PropTypes.string.isRequired
    }).isRequired,
};

export default ParameterBackupModal;
